import {
  formatarIdentificacaoObra,
  type ObraIdentificavel,
} from "@/shared/utils/formatar-identificacao-obra";
import type { FilterDefinition, FilterOption, FilterValue } from "@/ds";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoObra,
  CargaTrabalhoObrasData,
  CargaTrabalhoProfissionaisData,
} from "./carga-trabalho.types";
import { calcularConflitos, isObraCancelada, overlaps } from "./carga-trabalho.utils";

type CargaTrabalhoDados = CargaTrabalhoProfissionaisData | CargaTrabalhoObrasData;

/** Colunas filtráveis da Carga de Trabalho (Figma: Filtros - Carga de Trabalho). */
export const CARGA_FILTER = {
  cargo: "cargo",
  obra: "obra",
  conflito: "conflito",
  indisponibilidade: "indisponibilidade",
  fase: "fase",
  cancelada: "cancelada",
} as const;

const SIM = "sim";
const NAO = "nao";

const SIM_NAO: FilterOption[] = [
  { value: SIM, label: "Sim" },
  { value: NAO, label: "Não" },
];

const FASES: FilterOption[] = [
  { value: "planejamento", label: "Planejamento" },
  { value: "obra", label: "Obra" },
  { value: "fechamento", label: "Fechamento" },
];

function obraLabel(obra: ObraIdentificavel): string {
  return formatarIdentificacaoObra(obra);
}

function collectObras(data: CargaTrabalhoDados | null): CargaTrabalhoObra[] {
  if (!data) return [];
  if ("profissionais" in data) {
    return data.profissionais.flatMap((profissional) =>
      profissional.alocacoes.map((alocacao) => alocacao.obra),
    );
  }
  return data.obras;
}

function collectCargos(data: CargaTrabalhoDados | null): string[] {
  if (!data) return [];
  const cargos =
    "profissionais" in data
      ? data.profissionais.map((profissional) => profissional.time)
      : data.obras.flatMap((obra) => obra.profissionais.map((profissional) => profissional.time));
  return cargos.filter((time): time is string => Boolean(time));
}

/**
 * Colunas do painel; as opções de Cargo e Obra vêm dos dados carregados (sem filtro aplicado).
 * O Cargo é o campo exibido abaixo do nome do profissional (hoje, a coluna `time` do usuário).
 */
export function buildCargaFilterColumns(data: CargaTrabalhoDados | null): FilterDefinition[] {
  const cargos = [...new Set(collectCargos(data))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  const obras = new Map(collectObras(data).map((obra) => [obra.id, obra]));
  const obraOptions = [...obras.values()]
    .sort((a, b) => a.codigo.localeCompare(b.codigo, "pt-BR"))
    .map((obra) => ({ value: obra.id, label: obraLabel(obra) }));

  return [
    {
      key: CARGA_FILTER.cargo,
      label: "Cargo",
      type: "multiselect",
      options: cargos.map((time) => ({ value: time, label: time })),
    },
    {
      key: CARGA_FILTER.obra,
      label: "Obra",
      type: "multiselect",
      options: obraOptions,
    },
    { key: CARGA_FILTER.conflito, label: "Conflito", type: "select", options: SIM_NAO },
    {
      key: CARGA_FILTER.indisponibilidade,
      label: "Indisponibilidade",
      type: "select",
      options: SIM_NAO,
    },
    { key: CARGA_FILTER.fase, label: "Fase da alocação", type: "multiselect", options: FASES },
    { key: CARGA_FILTER.cancelada, label: "Obra cancelada", type: "select", options: SIM_NAO },
  ];
}

export function hasCargaFilters(value: FilterValue): boolean {
  return Object.values(value).some((values) => values.length > 0);
}

/** "Obra cancelada = Sim" só mostra algo com o indicador Cancelada ligado. */
export function filtraObraCancelada(value: FilterValue): boolean {
  return value[CARGA_FILTER.cancelada]?.[0] === SIM;
}

function selected(value: FilterValue, key: string): string[] {
  return value[key] ?? [];
}

/** Obra com alguma das fases escolhidas (a alocação precisa tocar a fase). */
function matchesFase(
  obra: CargaTrabalhoObra,
  fases: string[],
  alocacao?: { inicio: string; fim: string },
): boolean {
  if (fases.length === 0) return true;
  return fases.some((fase) => {
    const etapa = obra.etapas[fase as keyof CargaTrabalhoObra["etapas"]];
    if (!etapa) return false;
    return alocacao ? overlaps(etapa, alocacao) : true;
  });
}

function matchesObra(
  obra: CargaTrabalhoObra,
  value: FilterValue,
  alocacao?: { inicio: string; fim: string },
): boolean {
  const obras = selected(value, CARGA_FILTER.obra);
  const [cancelada] = selected(value, CARGA_FILTER.cancelada);
  if (obras.length > 0 && !obras.includes(obra.id)) return false;
  if (cancelada && (cancelada === SIM) !== isObraCancelada(obra)) return false;
  return matchesFase(obra, selected(value, CARGA_FILTER.fase), alocacao);
}

function filterProfissionais(
  data: CargaTrabalhoProfissionaisData,
  value: FilterValue,
): CargaTrabalhoProfissionaisData {
  const cargos = selected(value, CARGA_FILTER.cargo);
  const [conflito] = selected(value, CARGA_FILTER.conflito);
  const [indisponibilidade] = selected(value, CARGA_FILTER.indisponibilidade);
  const filtraAlocacoes =
    selected(value, CARGA_FILTER.obra).length > 0 ||
    selected(value, CARGA_FILTER.fase).length > 0 ||
    selected(value, CARGA_FILTER.cancelada).length > 0;

  const profissionais = data.profissionais
    .filter((profissional) => {
      if (cargos.length > 0 && !cargos.includes(profissional.time ?? "")) return false;
      if (conflito && (conflito === SIM) !== calcularConflitos(profissional.alocacoes).length > 0) {
        return false;
      }
      if (
        indisponibilidade &&
        (indisponibilidade === SIM) !== profissional.indisponibilidades.length > 0
      ) {
        return false;
      }
      return true;
    })
    .map((profissional) => ({
      ...profissional,
      alocacoes: profissional.alocacoes.filter((alocacao: CargaTrabalhoAlocacao) =>
        matchesObra(alocacao.obra, value, alocacao),
      ),
    }))
    .filter((profissional) => !filtraAlocacoes || profissional.alocacoes.length > 0);

  return { ...data, profissionais };
}

function filterObras(data: CargaTrabalhoObrasData, value: FilterValue): CargaTrabalhoObrasData {
  const cargos = selected(value, CARGA_FILTER.cargo);
  const fases = selected(value, CARGA_FILTER.fase);

  const obras = data.obras
    .filter((obra) => matchesObra(obra, value))
    .map((obra) => ({
      ...obra,
      profissionais: obra.profissionais.filter(
        (profissional) =>
          (cargos.length === 0 || cargos.includes(profissional.time ?? "")) &&
          matchesFase(obra, fases, profissional),
      ),
    }))
    .filter((obra) => (cargos.length === 0 && fases.length === 0) || obra.profissionais.length > 0);

  return { ...data, obras };
}

/**
 * Aplica os filtros do painel sobre os dados carregados. Na visão por Obras, Conflito e
 * Indisponibilidade não se aplicam: a consulta não traz as alocações completas de cada
 * profissional nem as indisponibilidades.
 */
export function applyCargaFilters<T extends CargaTrabalhoDados>(data: T, value: FilterValue): T {
  if (!hasCargaFilters(value)) return data;
  const filtered =
    "profissionais" in data ? filterProfissionais(data, value) : filterObras(data, value);
  return filtered as T;
}
